import React, { useState, useEffect } from 'react';
import { useImages } from '../contexts/ImageContext';
import GalleryCard from '../components/GalleryCard';
import FilterTabs from '../components/FilterTabs';
import LightboxModal from '../components/LightboxModal';
import AddImageModal from '../components/AddImageModal';

const Gallery = () => {
  const { images, addImage, deleteImage, getImagesByCategory } = useImages();
  const [filteredImages, setFilteredImages] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [selectedImage, setSelectedImage] = useState(null);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);

  const categories = ['All', 'Living', 'Bedroom', 'Kitchen', 'Office', 'Bathroom'];

  // Filter images when category or images change
  useEffect(() => {
    setFilteredImages(getImagesByCategory(selectedCategory));
  }, [selectedCategory, images, getImagesByCategory]);

  const handleImageClick = (image) => {
    setSelectedImage(image);
  };

  const handleAddImage = (newImage) => {
    addImage(newImage);
    setIsAddModalOpen(false);
  };

  const handleDeleteImage = (imageToDelete) => {
    if (window.confirm(`Are you sure you want to delete "${imageToDelete.title}"?`)) {
      deleteImage(imageToDelete.id);
    }
  };

  const handleNextImage = () => {
    if (!selectedImage || !filteredImages.length) return;
    const currentIndex = filteredImages.findIndex(img => img.id === selectedImage.id);
    const nextIndex = (currentIndex + 1) % filteredImages.length;
    setSelectedImage(filteredImages[nextIndex]);
  };

  const handlePrevImage = () => {
    if (!selectedImage || !filteredImages.length) return;
    const currentIndex = filteredImages.findIndex(img => img.id === selectedImage.id);
    const prevIndex = currentIndex === 0 ? filteredImages.length - 1 : currentIndex - 1;
    setSelectedImage(filteredImages[prevIndex]);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-black to-gray-900">
      {/* Hero Section - Mobile Optimized */}
      <section className="relative h-[300px] sm:h-[400px] md:h-[500px] lg:h-[600px] flex items-center justify-center overflow-hidden">
        {/* Background Overlay */}
        <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/30 to-black/50"></div>
        
        {/* Hero Content */}
        <div className="relative z-10 text-center px-4 sm:px-6 md:px-8">
          <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl xl:text-7xl font-bold mb-3 sm:mb-4 md:mb-6 tracking-tighter text-white">
            Gallery
          </h1>
          <p className="text-sm sm:text-base md:text-lg lg:text-xl max-w-xs sm:max-w-sm md:max-w-lg lg:max-w-2xl mx-auto text-gray-300">
            Explore our portfolio of exceptional interior design projects
          </p>
        </div>
      </section>

      {/* Add Image Button - Mobile Optimized */}
      <div className="px-4 sm:px-6 md:px-8 lg:px-16">
        <div className="max-w-7xl mx-auto flex justify-end mb-4 sm:mb-6">
          <button
            onClick={() => setIsAddModalOpen(true)}
            className="flex items-center gap-2 px-3 sm:px-4 py-2 rounded-full font-medium transition-all duration-300 text-xs sm:text-sm md:text-base bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700 text-white shadow-lg hover:shadow-xl hover:scale-105"
          >
            <span className="material-symbols-outlined text-sm sm:text-base">add</span>
            <span className="hidden sm:inline">Add Image</span>
            <span className="sm:hidden">Add</span>
          </button>
        </div>
      </div>

      {/* Filter Section - Mobile Optimized */}
      <section className="py-6 sm:py-8 md:py-12 px-4 sm:px-6 md:px-8 lg:px-16">
        <div className="max-w-7xl mx-auto">
          <FilterTabs
            categories={categories}
            activeCategory={selectedCategory}
            onCategoryChange={setSelectedCategory}
          />
        </div>
      </section>

      {/* Gallery Grid - Mobile First Responsive */}
      <section className="py-6 sm:py-8 md:py-12 px-4 sm:px-6 md:px-8 lg:px-16">
        <div className="max-w-7xl mx-auto">
          {filteredImages.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3 sm:gap-4 md:gap-6 lg:gap-8">
              {filteredImages.map((image) => (
                <GalleryCard
                  key={image.id}
                  image={image}
                  onImageClick={handleImageClick}
                  onDeleteClick={handleDeleteImage}
                />
              ))}
            </div>
          ) : (
            <div className="text-center py-12 sm:py-16">
              <span className="material-symbols-outlined text-4xl sm:text-5xl md:text-6xl text-gray-600 mb-4">image_not_supported</span>
              <h3 className="text-lg sm:text-xl md:text-2xl font-semibold mb-2 text-white">No images found</h3>
              <p className="text-sm sm:text-base text-gray-400">Try adding some images or changing the filter.</p>
            </div>
          )}
        </div>
      </section>

      {/* Stats Section - Mobile Optimized */}
      <section className="py-8 sm:py-12 md:py-16 px-4 sm:px-6 md:px-8 lg:px-16">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6 md:gap-8">
            <div className="text-center p-4 sm:p-6 bg-gray-800/50 backdrop-blur-sm rounded-2xl border border-gray-700">
              <div className="text-2xl sm:text-3xl md:text-4xl font-bold text-amber-400 mb-2">
                {images.length}
              </div>
              <div className="text-xs sm:text-sm text-gray-300">Total Projects</div>
            </div>
            <div className="text-center p-4 sm:p-6 bg-gray-800/50 backdrop-blur-sm rounded-2xl border border-gray-700">
              <div className="text-2xl sm:text-3xl md:text-4xl font-bold text-amber-400 mb-2">
                {categories.length - 1}
              </div>
              <div className="text-xs sm:text-sm text-gray-300">Categories</div>
            </div>
            <div className="text-center p-4 sm:p-6 bg-gray-800/50 backdrop-blur-sm rounded-2xl border border-gray-700">
              <div className="text-2xl sm:text-3xl md:text-4xl font-bold text-amber-400 mb-2">
                100%
              </div>
              <div className="text-xs sm:text-sm text-gray-300">Satisfaction</div>
            </div>
            <div className="text-center p-4 sm:p-6 bg-gray-800/50 backdrop-blur-sm rounded-2xl border border-gray-700">
              <div className="text-2xl sm:text-3xl md:text-4xl font-bold text-amber-400 mb-2">
                24/7
              </div>
              <div className="text-xs sm:text-sm text-gray-300">Support</div>
            </div>
          </div>
        </div>
      </section>

      {/* Lightbox Modal */}
      {selectedImage && (
        <LightboxModal
          image={selectedImage}
          images={filteredImages}
          onClose={() => setSelectedImage(null)}
          onNext={handleNextImage}
          onPrev={handlePrevImage}
        />
      )}

      {/* Add Image Modal */}
      <AddImageModal
        isOpen={isAddModalOpen}
        onClose={() => setIsAddModalOpen(false)}
        onAddImage={handleAddImage}
      />
    </div>
  );
};

export default Gallery;
